import { createSlice, PayloadAction, createSelector } from 'redux-starter-kit';
import { Resources, NamedObject, Mutations } from '@erecruit/types';

import { selectAppState } from '../../../App/state/selectors';
import { AppState } from '../../../App/state/reducer';

export interface AddNewShiftState {
  isLoading: boolean;
  error?: string;
  positions: Resources.PositionSearch[];
  totalPositionsCount: number;
  selectedPositionId: number;
  positionShiftTypes: Resources.ShiftType[];
  positionDepartments: NamedObject<number>[];
  selectedPosition: Resources.PositionSearch | null;
  shiftsCreated: boolean;
}

const initialState: AddNewShiftState = {
  isLoading: false,
  positions: [],
  totalPositionsCount: 0,
  selectedPositionId: -1,
  positionShiftTypes: [],
  positionDepartments: [],
  selectedPosition: null,
  shiftsCreated: false,
};

const AddNewShiftSlice = createSlice({
  slice: 'addNewShift',
  initialState: initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    searchPositions(state, action: PayloadAction<string>) {
      state.isLoading = true;
      state.shiftsCreated = false;
    },
    searchPositionsSuccess(
      state,
      action: PayloadAction<{
        positions: Resources.PositionSearch[];
        totalCount: number;
      }>,
    ) {
      const { positions, totalCount } = action.payload;
      state.positions = positions;
      state.totalPositionsCount = totalCount;
      state.isLoading = false;
    },
    searchPositionsFail(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    searchPositionShiftTypes(state, action: PayloadAction<number>) {
      const positionId = action.payload;
      const position = state.positions.find(
        position => position.id === positionId,
      );
      const positionDepartments = position?.companyDepartments || [];

      state.isLoading = true;
      state.selectedPosition = position || null;
      state.positionDepartments = positionDepartments;
      state.selectedPositionId = positionId;
    },
    searchPositionShiftTypesSuccess(
      state,
      action: PayloadAction<Resources.ShiftType[]>,
    ) {
      state.positionShiftTypes = action.payload;
      state.isLoading = false;
    },
    searchPositionShiftTypesFail(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    addShifts(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<
        Mutations.NewShiftsParams & {
          positionName: string;
        }
      >,
    ) {
      state.isLoading = true;
    },
    addShiftsSuccess(state) {
      state.shiftsCreated = true;
    },
    addShiftsError(state) {
      state.shiftsCreated = false;
    },
  },
});

const {
  actions,
  reducer,
  selectors: { getAddNewShift },
} = AddNewShiftSlice;

export const {
  searchPositions,
  searchPositionsSuccess,
  searchPositionsFail,
  searchPositionShiftTypes,
  searchPositionShiftTypesSuccess,
  searchPositionShiftTypesFail,
  addShifts,
  addShiftsSuccess,
  addShiftsError,
} = actions;

export const selectors = {
  selectAddNewShiftState: createSelector([selectAppState], getAddNewShift),
  selectSearchState: createSelector([selectAppState], (state: AppState) => ({
    data: state.addNewShift.positions,
    totalCount: state.addNewShift.totalPositionsCount,
    isLoading: state.addNewShift.isLoading,
  })),
};

export default reducer;
